/* eslint-disable @nx/enforce-module-boundaries */
import { CirclePlus, CloseIcon } from '@mybridge/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  HStack,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@mybridge/ui';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { formatCommaSeparated } from 'v4/lib/commons';
import { MyTestimonialsContext } from '../context';
import { ReceivedNoDataIll } from '../ill/received-no-data-ill';
import { ReceivedPendingNoDataIll } from '../ill/received-pending-no-data-ill';
import { MyTestimonialsUserCard } from '../user-card';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const MyTestimonialsReceivedListing = forwardRef(({ ...props }, ref) => {
  const {
    receivedTestimonials,
    receivedTestimonialsQuery,
    receivedTestimonialsPendingRequests,
    receivedTestimonialsPendingRequestsQuery,
    tab,
    addToProfileMutation,
    addToProfile,
    removeFromProfileMutation,
    removeFromProfile,
    deleteTestimonialMutation,
    deleteTestimonial,
    selected,
    setSelected,
  } = useContext(MyTestimonialsContext);
  const { t } = useTranslator();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [testimonialToDelete, setTestimonialToDelete] = useState(null);
  const [testimonialToDeletePermanent, setTestimonialToDeletePermanent] = useState(null);
  const cancelRef = useRef();
  const cancelDeleteRef = useRef();
  useEffect(() => {
    if (tab === 0) {
      receivedTestimonialsQuery?.refetch?.();
      receivedTestimonialsPendingRequestsQuery?.refetch?.();
    }
  }, [tab]);

  const visibleCount = receivedTestimonials?.filter?.(
    (rt) => rt.is_added_to_profile
  )?.length;
  const hiddenCount = receivedTestimonials?.filter?.(
    (rt) => !rt.is_added_to_profile
  )?.length;
  return (
    <Stack spacing={4}>
      <Stack p={4} borderRadius={5} border="1px solid" borderColor="gray.200">
        {receivedTestimonialsPendingRequests?.length ? (
          <>
            <Box display="flex" alignItems="center">
              <Text display="inline">{t('profileTestimonials.pendingTestimonials') || "Pending Testimonials"}</Text>
              <Text
                py={0.5}
                px={2}
                // size="sm"
                color="white"
                ml={1}
                bgColor="red.500"
                borderRadius="full"
                // display="inline"
                height="20px"
                width="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
              >
                {receivedTestimonialsPendingRequests?.length}
              </Text>
            </Box>
            {receivedTestimonialsPendingRequests?.map?.((testimonial, tIndex) => (
              <Stack
                {...(tIndex < receivedTestimonialsPendingRequests?.length - 1
                  ? { borderBottom: '1px solid', borderColor: 'gray.200' }
                  : {})}
                p={4}
                key={tIndex}
                _hover={{
                  background: '#E2E2E2',
                }}
              >
                <MyTestimonialsUserCard
                  actions={
                    <HStack justifyContent="flex-end">
                      <Button
                        color="brandPrimary.500"
                        variant="ghost"
                        contSize
                        leftIcon={
                          <CloseIcon width={'20'} height={'20'} color="red" />
                        }
                        onClick={(e) => {
                          setTestimonialToDelete(testimonial);
                          onOpen();
                        }}
                        isLoading={
                          selected?.id === testimonial?.id &&
                          deleteTestimonialMutation?.isLoading
                        }
                      >
                        {t('profileNetwork.ignore') || "Ignore"}
                      </Button>
                      <Button
                        color="brandPrimary.500"
                        variant="ghost"
                        contSize
                        leftIcon={
                          <CirclePlus color="blue" width="20" height="20" />
                        }
                        onClick={(e) => {
                          setSelected(testimonial);
                          addToProfile(testimonial);
                        }}
                        isLoading={
                          selected?.id === testimonial?.id &&
                          addToProfileMutation?.isLoading
                        }
                      >
                        {t('profileTestimonials.addToProfile') || "Add to profile"}
                      </Button>
                    </HStack>
                  }
                  user={testimonial?.giver}
                  message={testimonial?.testimonial}
                  createdAt={testimonial?.created_at}
                ></MyTestimonialsUserCard>
              </Stack>
            ))}
          </>
        ) : (
          <Stack>
            <Text display="inline">{t('profileTestimonials.pendingTestimonials') || "Pending Testimonials"}</Text>
            <HStack justifyContent="center">
              <Stack alignItems="center">
                <ReceivedPendingNoDataIll />
                <Text display="inline">{t('profileTestimonials.noPendingTestimonials') || "You have no pending testimonials"}</Text>
              </Stack>
            </HStack>
          </Stack>
        )}
      </Stack>
      <Stack p={4} borderRadius={5} border="1px solid" borderColor="gray.200">
        {receivedTestimonials?.length ? (
          <>
            <Box>
              <Text display="inline">
                {t('profileTestimonials.youHave') || "You have"} {receivedTestimonials?.length} {t('profileTestimonials.testimonials') || "Testimonials"}{' '}
                {visibleCount || hiddenCount
                  ? `(${formatCommaSeparated([
                      visibleCount ? `${visibleCount} visible` : '',
                      hiddenCount ? `${hiddenCount} hidden` : '',
                    ])})`
                  : ''}
              </Text>
              {/* <Text display="inline">{receivedTestimonials?.length}</Text> */}
            </Box>
            {receivedTestimonials?.map?.((testimonial, tIndex) => (
              <HStack
                {...(tIndex < receivedTestimonials?.length - 1
                  ? { borderBottom: '1px solid', borderColor: 'gray.200' }
                  : {})}
                p={4}
                key={tIndex}
                alignItems="flex-start"
                _hover={{
                  background: '#E2E2E2',
                }}
              >
                <Tooltip label="Make visible"  placement='bottom'>
                  <Box as="span">
                  <Checkbox
                    onChange={(e) => {
                      if (testimonial?.is_added_to_profile) {
                        removeFromProfile(testimonial);
                      } else {
                        addToProfile(testimonial);
                      }
                    }}
                    mt={3}
                    isChecked={testimonial?.is_added_to_profile}
                  />
                  </Box>
                </Tooltip>
                <Stack flex={1}>
                  <MyTestimonialsUserCard
                    actions={
                      <HStack>
                        <Button
                          color="brandPrimary.500"
                          variant="ghost"
                          leftIcon={
                            <CloseIcon color="red" width="20" height="20" />
                          }
                          onClick={(e) => {
                            setTestimonialToDeletePermanent(testimonial);
                            onDeleteOpen();
                          }}
                          isLoading={
                            selected?.id === testimonial?.id &&
                            deleteTestimonialMutation?.isLoading
                          }
                        >
                          {t('profileTestimonials.deleteThisTestimonial') || "Delete this testimonial"}
                        </Button>
                      </HStack>
                    }
                    user={testimonial?.giver}
                    message={testimonial?.testimonial}
                    createdAt={testimonial?.created_at}
                  />
                </Stack>
              </HStack>
            ))}
          </>
        ) : (
          <Stack>
            <Text display="inline">
              {/* {t('profileTestimonials.testimonialSummary') || "You have 0 testimonials (0 visible, 0 hidden)"} */}
              You don't have any testimonials.
            </Text>
            <HStack justifyContent="center">
              <Stack alignItems="center">
                <ReceivedNoDataIll />
                <Text display="inline">{t('profileTestimonials.noTestimonials') || "You don't have any testimonials"}</Text>
              </Stack>
            </HStack>
          </Stack>
        )}
      </Stack>

      {/* Warning Dialog for Ignore Action */}
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {"Ignore Testimonial"}
          </AlertDialogHeader>
          <AlertDialogBody>
            {"Are you sure you want to ignore this testimonial? This action cannot be undone."}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={onClose}
              borderRadius="50px"
              variant="secondary"
            >
              {t('buttons.cancel') || "Cancel"}
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              borderRadius="50px"
              onClick={() => {
                if (testimonialToDelete) {
                  setSelected(testimonialToDelete);
                  deleteTestimonial?.(testimonialToDelete);
                  setTestimonialToDelete(null);
                }
                onClose();
              }}
            >
              {t('profileNetwork.ignore') || "Ignore"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Warning Dialog for Delete Action */}
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelDeleteRef}
        onClose={onDeleteClose}
        isOpen={isDeleteOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {"Delete Testimonial"}
          </AlertDialogHeader>
          <AlertDialogBody>
            {"Are you sure you want to delete this testimonial? This action cannot be undone."}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelDeleteRef}
              onClick={onDeleteClose}
              borderRadius="50px"
              variant="secondary"
            >
              {t('buttons.cancel') || "Cancel"}
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              borderRadius="50px"
              onClick={() => {
                if (testimonialToDeletePermanent) {
                  setSelected(testimonialToDeletePermanent);
                  deleteTestimonial?.(testimonialToDeletePermanent);
                  setTestimonialToDeletePermanent(null);
                }
                onDeleteClose();
              }}
            >
              {t('profileTestimonials.deleteThisTestimonial') || "Delete this testimonial"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Stack>
  );
});
